import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Home,
  Calendar,
  User,
  Building,
  PoundSterling,
  ArrowRight,
  RefreshCw,
  XCircle,
  Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getErrorMessage } from '../utils/errorUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReverseEngineeringPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
        setResult(null);
        setError(null);
      } else {
        toast.error('Please upload a PDF file');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        setResult(null);
        setError(null);
      } else {
        toast.error('Please upload a PDF file');
      }
    }
  };

  const processFile = async () => {
    if (!file) return;

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        const increment = Math.random() * 15;
        const newProgress = Math.min(prev + increment, 90);
        
        // Update message based on progress
        if (newProgress < 20) {
          setProgressMessage('Uploading PDF...');
        } else if (newProgress < 40) {
          setProgressMessage('Analyzing document structure...');
        } else if (newProgress < 60) {
          setProgressMessage('Extracting property details...');
        } else if (newProgress < 80) {
          setProgressMessage('Processing inspection findings...');
        } else {
          setProgressMessage('Creating inspection record...');
        }
        
        return newProgress;
      });
    }, 500);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('auto_create_inspection', 'true');

      const response = await axios.post(
        `${API}/reverse-engineer/upload-pdf`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      clearInterval(progressInterval);
      setProgress(100);
      setProgressMessage('Complete!');
      setResult(response.data);
      
      if (response.data.inspection_created?.success) {
        toast.success('Inspection created successfully from PDF!');
      } else if (response.data.success) {
        toast.success('PDF analyzed successfully!');
      }

    } catch (err) {
      clearInterval(progressInterval);
      setProgress(0);
      console.error('Processing error:', err);
      const errorMessage = getErrorMessage(err, 'Failed to process PDF');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setProgressMessage('');
  };

  const viewInspection = () => {
    if (result?.inspection_created?.inspection_id) {
      navigate(`/inspections/${result.inspection_created.inspection_id}`);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reverse Engineering</h1>
            <p className="text-slate-600 mt-1">Upload a PDF report to extract and create inspection data</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/inspections')}>
            Back to Inspections
          </Button>
        </div>

        {/* Upload Area */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-teal-600" />
              Upload PDF Report
            </CardTitle>
            <CardDescription>
              Supports all property report types: RICS Red Book, HomeBuyer, Building Survey, etc.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!file ? (
              <div
                className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  dragActive
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                data-testid="upload-dropzone"
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="file-input"
                />
                <div className="space-y-4">
                  <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                    <FileText className="h-8 w-8 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-slate-700">
                      Drop your PDF here or click to browse
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Maximum file size: 10MB
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Selected File */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <FileText className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  {!processing && !result && (
                    <Button variant="ghost" size="sm" onClick={resetUpload}>
                      <XCircle className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>

                {/* Processing Progress */}
                <AnimatePresence>
                  {processing && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{progressMessage}</span>
                        <span className="font-medium text-teal-600">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <p className="text-xs text-slate-500 text-center">
                        AI is analyzing your document... This may take a moment.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Process Button */}
                {!result && !error && (
                  <Button
                    onClick={processFile}
                    disabled={processing}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    data-testid="process-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Process PDF & Create Inspection
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-100 rounded-full">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-800">Processing Failed</h3>
                      <p className="text-red-700 mt-1">{error}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                        onClick={resetUpload}
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success Result */}
        <AnimatePresence>
          {result && result.success && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Success Banner */}
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 rounded-full">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-800">
                        {result.inspection_created?.success
                          ? 'Inspection Created Successfully!'
                          : 'PDF Analyzed Successfully!'}
                      </h3>
                      <p className="text-green-700 mt-1">
                        {result.inspection_created?.findings_created || 0} findings extracted
                      </p>
                    </div>
                    {result.inspection_created?.inspection_id && (
                      <Button
                        onClick={viewInspection}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="view-inspection-btn"
                      >
                        View Inspection
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Extracted Data Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Extracted Data Summary</CardTitle>
                  <CardDescription>
                    Confidence: {result.validation?.confidence || result.extracted_data?.extraction_confidence || 0}%
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Property Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Home className="h-5 w-5 text-teal-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Property Address</p>
                        <p className="font-medium">
                          {result.extracted_data?.property_address || 'Not extracted'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Building className="h-5 w-5 text-teal-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Property Type</p>
                        <p className="font-medium capitalize">
                          {result.extracted_data?.property_details?.property_type?.replace('_', ' ') || 'Not extracted'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Calendar className="h-5 w-5 text-teal-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Inspection Date</p>
                        <p className="font-medium">
                          {result.extracted_data?.inspection_date || 'Not extracted'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <User className="h-5 w-5 text-teal-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Inspector</p>
                        <p className="font-medium">
                          {result.extracted_data?.inspector_name || 'Not extracted'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Valuation */}
                  {result.extracted_data?.valuation?.market_value && (
                    <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-200">
                      <div className="flex items-center gap-3">
                        <PoundSterling className="h-6 w-6 text-teal-600" />
                        <div>
                          <p className="text-sm text-teal-700">Market Value</p>
                          <p className="text-2xl font-bold text-teal-800">
                            {formatCurrency(result.extracted_data.valuation.market_value)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Findings Preview */}
                  {result.extracted_data?.findings?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3">
                        Extracted Findings ({result.extracted_data.findings.length})
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {result.extracted_data.findings.slice(0, 5).map((finding, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <Badge
                                variant="outline"
                                className={
                                  finding.condition_rating <= 2
                                    ? 'bg-red-100 text-red-800 border-red-200'
                                    : finding.condition_rating === 3
                                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                                    : 'bg-green-100 text-green-800 border-green-200'
                                }
                              >
                                {finding.condition_rating}/5
                              </Badge>
                              <span className="font-medium">{finding.element_name}</span>
                            </div>
                            {finding.estimated_cost && (
                              <span className="text-sm text-slate-600">
                                {formatCurrency(finding.estimated_cost)}
                              </span>
                            )}
                          </div>
                        ))}
                        {result.extracted_data.findings.length > 5 && (
                          <p className="text-sm text-slate-500 text-center py-2">
                            + {result.extracted_data.findings.length - 5} more findings
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Validation Warnings */}
                  {result.validation?.warnings?.length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Extraction Notes</p>
                          <ul className="mt-2 space-y-1">
                            {result.validation.warnings.map((warning, index) => (
                              <li key={index} className="text-sm text-amber-700">
                                • {warning}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={resetUpload}
                      className="flex-1"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Another PDF
                    </Button>
                    {result.inspection_created?.inspection_id && (
                      <Button
                        onClick={viewInspection}
                        className="flex-1 bg-teal-600 hover:bg-teal-700"
                      >
                        View Inspection
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instructions Card */}
        {!file && !result && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3">
                    <span className="text-xl font-bold text-teal-600">1</span>
                  </div>
                  <h4 className="font-medium">Upload PDF</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Drop or select any property inspection or valuation report
                  </p>
                </div>
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3">
                    <span className="text-xl font-bold text-teal-600">2</span>
                  </div>
                  <h4 className="font-medium">AI Analysis</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Our AI extracts property details, findings, and valuations
                  </p>
                </div>
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3">
                    <span className="text-xl font-bold text-teal-600">3</span>
                  </div>
                  <h4 className="font-medium">Auto-Create</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Inspection record is created automatically with all data
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
